// @givmo/app-admin — the company giving-publication's module vocabulary + tolerant decode (CW-WEB4).
//
// ══ WHAT THIS SCREEN IS ═══════════════════════════════════════════════════════════════════════
// A CONSENT surface, not a dashboard. Nothing here is a figure: the row the Owner edits holds only
// WHICH BLOCKS may be shown, and every actual number is computed at read time from the live
// match/donation domain and k-anonymity floored there. So this module models a selection and never
// an aggregate — there is no money in this file, and there must never be.
//
// ══ THE MODULE VOCABULARY IS THE BACKEND'S ════════════════════════════════════════════════════
// The six ids below are CW-BE6's `KNOWN_MODULES` verbatim (`company_giving_publication_entities.py`),
// and {@link DEFAULT_GIVING_MODULES} is its `DEFAULT_MODULES` — five of the six, participation
// excluded. Labels and descriptions are this screen's; the IDS are wire values and must match.
//
// ⚠ SELECTING `participation_rate` IS the org opt-in (D-CW-5). There is no second flag, because two
// controls for one decision is how they drift apart — so the checkbox carries the whole decision and
// its description has to say what publishing a rate over a small workforce means.
//
// ══ WHY AN UNRECOGNISED MODULE IS KEPT, NOT DROPPED ═══════════════════════════════════════════
// The PUT is whole-state, so a build that dropped a module id it did not recognise would REVOKE
// publication of a block the Owner never asked to revoke — a silent change to what their public card
// shows, made by a client that was merely out of date. {@link decodeGivingPublication} therefore
// keeps unknown ids in `unrecognizedModules` and the save path sends them back unchanged, which is
// the no-op direction. The tab renders a quiet line saying how many there are, so "kept unchanged"
// is stated rather than hidden.

/** A block a company may disclose. `id` is the wire value; everything else is this screen's copy. */
export interface GivingModuleDefinition {
  id: string;
  label: string;
  description: string;
  testId: string;
}

/**
 * The six blocks CW-BE6 admits, in the order the screen offers them: the two headline figures, then
 * where the money went, then how it got there, then — last and unticked by default — the rate.
 */
export const GIVING_MODULES: readonly GivingModuleDefinition[] = [
  {
    id: 'total_donated',
    label: 'Total donated',
    description:
      'What your company gave over the published period — the matches you approved plus your ' +
      'company’s own direct gifts.',
    testId: 'giving-card-module-total-donated',
  },
  {
    id: 'charities_supported',
    label: 'Charities supported',
    description: 'How many distinct charities that giving reached. A count, never the list.',
    testId: 'giving-card-module-charities-supported',
  },
  {
    id: 'top_charities',
    label: 'Top charities',
    description:
      'Your most-supported charities, ranked, with what your company gave each. A charity only a ' +
      'couple of people gave to is left out of the list rather than the list being hidden.',
    testId: 'giving-card-module-top-charities',
  },
  {
    id: 'featured_causes',
    label: 'Featured causes',
    description: 'The causes carried by the charities that appear in your top-charities list.',
    testId: 'giving-card-module-featured-causes',
  },
  {
    id: 'employer_match',
    label: 'Employer match',
    description:
      'Your match program in summary: the dollars your company matched, and how many employees ' +
      'received a match.',
    testId: 'giving-card-module-employer-match',
  },
  {
    id: 'participation_rate',
    label: 'Participation rate',
    description:
      'The share of enrolled employees taking part, as a whole percentage. Off unless you turn it ' +
      'on: a rate over a small workforce says more about individuals than a total does.',
    testId: 'giving-card-module-participation-rate',
  },
];

/** Every id this build understands, for the unknown-module split in {@link decodeGivingPublication}. */
const KNOWN_MODULE_IDS: ReadonlySet<string> = new Set(GIVING_MODULES.map((module) => module.id));

/**
 * What a company publishes when it has never chosen: everything EXCEPT the participation rate.
 * Mirrors CW-BE6's `DEFAULT_MODULES`, and the backend applies the same default server-side — this
 * copy exists so the unsaved screen shows the same ticks the server would have used, not so the
 * client decides the default.
 */
export const DEFAULT_GIVING_MODULES: readonly string[] = GIVING_MODULES.filter(
  (module) => module.id !== 'participation_rate',
).map((module) => module.id);

/** The decoded publication state the tab renders. */
export interface GivingPublicationState {
  /** Whether the card may be shown publicly right now. Re-read by the payload on every fetch. */
  published: boolean;
  /** The selected blocks THIS build can render a control for, in wire order. */
  modules: string[];
  /**
   * Selected blocks this build does not recognise — kept so a whole-state save re-sends them
   * unchanged rather than silently revoking them. Never rendered as controls.
   */
  unrecognizedModules: string[];
  /**
   * The opaque `wgt_` embed id, or `null`. Null in exactly two situations, and the tab distinguishes
   * them: before the first save (nothing has been minted) and after a save whose best-effort mint
   * failed (CW-BE6 leaves the publication intact and unbound, and the next save retries).
   */
  widgetId: string | null;
  /** ISO instant of the most recent publish, or `null`. Cleared on unpublish, never kept as a trace. */
  publishedAt: string | null;
}

/** The zero-state a never-saved company reads as — and the fallback for an unreadable body. */
export function zeroGivingPublicationState(): GivingPublicationState {
  return {
    published: false,
    modules: [...DEFAULT_GIVING_MODULES],
    unrecognizedModules: [],
    widgetId: null,
    publishedAt: null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A non-empty trimmed string, or `null`. Anything else — number, object, blank — is not a value. */
function optionalString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Decode `CompanyGivingPublicationOut`. Tolerant reader: never throws, and every unreadable field
 * degrades toward the state that discloses LESS.
 *
 * ⚠ `published` is tested `=== true`, not for truthiness. A string, number or object arriving on
 * that boolean is a shape this build does not understand, and reading it as "yes" would render this
 * screen as though the company had consented to publication when nothing said so. The restrictive
 * answer is the safe one for a consent flag, which is the opposite of the usual tolerant-reader
 * direction and the reason it is spelled out.
 *
 * `modules` from an unreadable body falls back to {@link DEFAULT_GIVING_MODULES} rather than to an
 * empty list: empty is a state the backend refuses, and offering the Owner a screen they cannot save
 * would be a worse answer than offering them the server's own default.
 */
export function decodeGivingPublication(body: unknown): GivingPublicationState {
  if (!isRecord(body)) return zeroGivingPublicationState();

  const rawModules = Array.isArray(body.modules)
    ? body.modules.map(optionalString).filter((id): id is string => id !== null)
    : [];

  // De-duplicated preserving first-seen order, matching the backend's own normalization.
  const seen = new Set<string>();
  const modules: string[] = [];
  const unrecognizedModules: string[] = [];
  for (const id of rawModules) {
    if (seen.has(id)) continue;
    seen.add(id);
    (KNOWN_MODULE_IDS.has(id) ? modules : unrecognizedModules).push(id);
  }

  return {
    published: body.published === true,
    modules:
      modules.length === 0 && unrecognizedModules.length === 0
        ? [...DEFAULT_GIVING_MODULES]
        : modules,
    unrecognizedModules,
    widgetId: optionalString(body.widget_id),
    publishedAt: optionalString(body.published_at),
  };
}

/**
 * Whether this selection can be saved at all.
 *
 * The backend refuses an EMPTY module list on either side of the publish switch, so an Owner who
 * unticks everything has built a body that 422s. Blocking Save here — with the reason on screen —
 * is the client half of that rule; it is not a second source of truth, and the server still decides.
 *
 * Unrecognised modules deliberately do NOT satisfy it: a list this build cannot show the Owner is
 * not a selection the Owner made on this screen.
 */
export function isSaveableSelection(modules: readonly string[]): boolean {
  return modules.some((id) => KNOWN_MODULE_IDS.has(id));
}

/**
 * The wire `modules` list for a save: the Owner's visible selection in the screen's canonical order,
 * followed by any unrecognised ids carried through untouched.
 *
 * Canonical order rather than click order so two Owners who tick the same boxes send the same body —
 * the backend compares module sets order-insensitively, but a stable body keeps a diffed request log
 * readable.
 */
export function toSaveModules(state: GivingPublicationState): string[] {
  const selected = new Set(state.modules);
  return [
    ...GIVING_MODULES.filter((module) => selected.has(module.id)).map((module) => module.id),
    ...state.unrecognizedModules,
  ];
}
